<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceHeader',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'created', 'deleted']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
    invoiceNumber: {
      type: String,
      required: false,
    },
    formContext: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    newTransaction: $fn.value.pgettext('Invoice', 'New Invoice'),
    invoiceKind: {
      incoming: $fn.value.pgettext('InvoiceKind', 'Incoming Invoice'),
      outgoing: $fn.value.pgettext('InvoiceKind', 'Outgoing Invoice'),
    },
    createCorrectorTooltip: $fn.value.pgettext(
      'Invoice',
      'Create corrector invoice'
    ),
    createStornoTooltip: $fn.value.pgettext('Invoice', 'Create storno invoice'),
    createPassThroughTooltip: $fn.value.pgettext(
      'Invoice|Tooltip',
      'Rebilling the line items to a designated client.'
    ),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    if (loaded.value) {
      return messages.value.invoiceKind[props.invoice.invoice_kind];
    }
    return null;
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return (
        props.invoiceNumber ||
        (props.invoice.isNew
          ? messages.value.newTransaction
          : props.invoice.invoice_number)
      );
    }
    return null;
  });
  const createCorrectorTooltip = computed(() => {
    if (loaded.value) {
      return (
        _.get(props.invoice, 'meta.modifiable.message') ||
        messages.value.createCorrectorTooltip
      );
    }
    return null;
  });
  const createStornoTooltip = computed(() => {
    if (loaded.value) {
      return (
        _.get(props.invoice, 'meta.reversible.message') ||
        messages.value.createStornoTooltip
      );
    }
    return null;
  });
  const invoiceIsReversible = computed(() => {
    return _.get(props.invoice, 'meta.reversible.result');
  });
  const invoiceIsModifiable = computed(() => {
    return _.get(props.invoice, 'meta.modifiable.result');
  });
  const invoiceIsRegistrable = computed(() => {
    return _.get(props.invoice, 'meta.registrable.result');
  });
  const invoicePassThroughIsEnabled = computed(() => {
    return (
      _.get(props.invoice, 'meta.pass_through_enabled') === true ||
      _.get(props.invoice, 'meta.pass_through_enabled.result')
    );
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );
  watch(
    () => props.invoiceNumber,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoiceNumber');
    }
  );
  watch(
    () => props.formContext,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formContext');
    }
  );

  const act_3573211234170528_emit_vzkehtve0_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_3350181908999980_emit_7o9tt5oyx_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5072463219534047_emit_3zl8mj4yi_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5450657804282831_emit_xxjvly9mt_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_995920279152457_emit_g4h71xdb5_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_995920279152457_close_wjk5keq57_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_995920279152457_showModal_c5a38l752_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceEditor',
      props: {
        invoice: _.cloneDeep(props.invoice),
      },
    };
  };
  const act_8434031504612555_emit_7vrv2x1tf_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_142876513986814_emit_1yf8ysi89_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4619326685596221_showModal_lxpk5k6xt_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceLogMessageViewer',
      props: {
        invoiceId: props.invoice.id,
      },
    };
  };
  const act_5071673642893579_emit_jypzo8yip_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_5071673642893579_close_20z2rv9c1_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_5071673642893579_showModal_zvayy9rbv_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        invoice: props.invoice,
      },
    };
  };
  const act_7200560637235153_emit_r12xr7aka_getActionArgs = async ($event) => {
    return {
      event: 'created',
      value: $event.data.response.data,
    };
  };
  const act_7200560637235153_request_nw3levkax_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoice',
      data: { invoice_id: props.invoice.id, invoice_type: 'copy' },
    };
  };
  const act_2820580198023098_emit_jdvrnss4b_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2820580198023098_close_ixx8o8f5c_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_2820580198023098_showModal_pfw8tlge0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreatePassThroughInvoice',
      props: {
        invoice: props.invoice,
      },
    };
  };
  const act_7708892537216822_emit_dg67dej3e_getActionArgs = async ($event) => {
    return {
      event: 'created',
      value: $event.data.response.data,
    };
  };
  const act_7708892537216822_request_m7h3c8r0x_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoice',
      data: { invoice_id: props.invoice.id, invoice_type: 'storno' },
    };
  };
  const act_3708615633814391_emit_tz6b7jd61_getActionArgs = async ($event) => {
    return {
      event: 'created',
      value: $event.data.response.data,
    };
  };
  const act_3708615633814391_request_iw01kndzd_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoice',
      data: {
        invoice_id: props.invoice.id,
        invoice_type: 'correction',
        invoice_lines: _.map(
          props.formContext.selectedInvoiceLines,
          (p) => p.id
        ).join(','),
      },
    };
  };
  const act_540979853027208_emit_aoo9btftv_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_8845268238478512_emit_siy2j1ef6_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };
  const act_8646800130370252_showModal_nfu1gboed_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'EntityEvents',
      props: {
        entityId: props.invoice.id,
      },
    };
  };

  const act_3573211234170528_emit_vzkehtve0 = new actionTypes.EmitAction({
    actionArgs: act_3573211234170528_emit_vzkehtve0_getActionArgs,
  });
  const inst_evh_3573211234170528_success_tzun5n9gv =
    new actionTypes.EventHandlerImpl({
      action: act_3573211234170528_emit_vzkehtve0,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_3573211234170528_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3573211234170528_success_tzun5n9gv],
    event: 'success',
  });
  const act_3350181908999980_emit_7o9tt5oyx = new actionTypes.EmitAction({
    actionArgs: act_3350181908999980_emit_7o9tt5oyx_getActionArgs,
  });
  const inst_evh_3350181908999980_success_1ln8b5d1j =
    new actionTypes.EventHandlerImpl({
      action: act_3350181908999980_emit_7o9tt5oyx,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_3350181908999980_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3350181908999980_success_1ln8b5d1j],
    event: 'success',
  });
  const act_5072463219534047_emit_3zl8mj4yi = new actionTypes.EmitAction({
    actionArgs: act_5072463219534047_emit_3zl8mj4yi_getActionArgs,
  });
  const inst_evh_5072463219534047_success_intrz1pyx =
    new actionTypes.EventHandlerImpl({
      action: act_5072463219534047_emit_3zl8mj4yi,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_5072463219534047_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5072463219534047_success_intrz1pyx],
    event: 'success',
  });
  const act_5450657804282831_emit_xxjvly9mt = new actionTypes.EmitAction({
    actionArgs: act_5450657804282831_emit_xxjvly9mt_getActionArgs,
  });
  const inst_evh_5450657804282831_success_rlm129vnb =
    new actionTypes.EventHandlerImpl({
      action: act_5450657804282831_emit_xxjvly9mt,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_5450657804282831_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5450657804282831_success_rlm129vnb],
    event: 'success',
  });
  const act_995920279152457_emit_g4h71xdb5 = new actionTypes.EmitAction({
    actionArgs: act_995920279152457_emit_g4h71xdb5_getActionArgs,
  });
  const inst_evh_995920279152457_close_wjk5keq57 =
    new actionTypes.EventHandlerImpl({
      action: act_995920279152457_emit_g4h71xdb5,
      event: 'close',
      when: inst_evh_995920279152457_close_wjk5keq57_getWhen,
      displayName: 'emit',
    });
  const act_995920279152457_showModal_c5a38l752 =
    new actionTypes.ShowModalAction({
      actionArgs: act_995920279152457_showModal_c5a38l752_getActionArgs,
      events: [inst_evh_995920279152457_close_wjk5keq57],
    });
  const inst_evh_995920279152457_clickPreventStop_w0yj2wtlq =
    new actionTypes.EventHandlerImpl({
      action: act_995920279152457_showModal_c5a38l752,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_995920279152457_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_995920279152457_clickPreventStop_w0yj2wtlq],
      event: 'click.prevent.stop',
    });
  const act_8434031504612555_emit_7vrv2x1tf = new actionTypes.EmitAction({
    actionArgs: act_8434031504612555_emit_7vrv2x1tf_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_8434031504612555_clickPreventStop_6eg7ssqp2 =
    new actionTypes.EventHandlerImpl({
      action: act_8434031504612555_emit_7vrv2x1tf,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8434031504612555_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8434031504612555_clickPreventStop_6eg7ssqp2],
      event: 'click.prevent.stop',
    });
  const act_142876513986814_emit_1yf8ysi89 = new actionTypes.EmitAction({
    actionArgs: act_142876513986814_emit_1yf8ysi89_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_142876513986814_reload_2ujswgocl =
    new actionTypes.EventHandlerImpl({
      action: act_142876513986814_emit_1yf8ysi89,
      event: 'reload',
      displayName: 'reload',
    });
  const inst_evh_142876513986814_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_142876513986814_reload_2ujswgocl],
    event: 'reload',
  });
  const act_4619326685596221_showModal_lxpk5k6xt =
    new actionTypes.ShowModalAction({
      actionArgs: act_4619326685596221_showModal_lxpk5k6xt_getActionArgs,
    });
  const inst_evh_4619326685596221_clickPreventStop_ix0e6dw4d =
    new actionTypes.EventHandlerImpl({
      action: act_4619326685596221_showModal_lxpk5k6xt,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_4619326685596221_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4619326685596221_clickPreventStop_ix0e6dw4d],
      event: 'click.prevent.stop',
    });
  const act_5071673642893579_emit_jypzo8yip = new actionTypes.EmitAction({
    actionArgs: act_5071673642893579_emit_jypzo8yip_getActionArgs,
  });
  const inst_evh_5071673642893579_close_20z2rv9c1 =
    new actionTypes.EventHandlerImpl({
      action: act_5071673642893579_emit_jypzo8yip,
      event: 'close',
      when: inst_evh_5071673642893579_close_20z2rv9c1_getWhen,
      displayName: 'emit',
    });
  const act_5071673642893579_showModal_zvayy9rbv =
    new actionTypes.ShowModalAction({
      actionArgs: act_5071673642893579_showModal_zvayy9rbv_getActionArgs,
      displayName: 'registerInvoice',
      events: [inst_evh_5071673642893579_close_20z2rv9c1],
    });
  const inst_evh_5071673642893579_click_gt4vfdi5h =
    new actionTypes.EventHandlerImpl({
      action: act_5071673642893579_showModal_zvayy9rbv,
      event: 'click',
      displayName: 'registerInvoice',
    });
  const inst_evh_5071673642893579_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5071673642893579_click_gt4vfdi5h],
    event: 'click',
  });
  const act_7200560637235153_emit_r12xr7aka = new actionTypes.EmitAction({
    actionArgs: act_7200560637235153_emit_r12xr7aka_getActionArgs,
  });
  const inst_evh_7200560637235153_success_pmyyy51mm =
    new actionTypes.EventHandlerImpl({
      action: act_7200560637235153_emit_r12xr7aka,
      event: 'success',
      displayName: 'emit',
    });
  const act_7200560637235153_request_nw3levkax = new actionTypes.RequestAction({
    actionArgs: act_7200560637235153_request_nw3levkax_getActionArgs,
    displayName: 'createInvoicecopy',
    events: [inst_evh_7200560637235153_success_pmyyy51mm],
  });
  const inst_evh_7200560637235153_click_1v4czf23a =
    new actionTypes.EventHandlerImpl({
      action: act_7200560637235153_request_nw3levkax,
      event: 'click',
      displayName: 'createInvoicecopy',
    });
  const inst_evh_7200560637235153_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7200560637235153_click_1v4czf23a],
    event: 'click',
  });
  const act_2820580198023098_emit_jdvrnss4b = new actionTypes.EmitAction({
    actionArgs: act_2820580198023098_emit_jdvrnss4b_getActionArgs,
  });
  const inst_evh_2820580198023098_close_ixx8o8f5c =
    new actionTypes.EventHandlerImpl({
      action: act_2820580198023098_emit_jdvrnss4b,
      event: 'close',
      when: inst_evh_2820580198023098_close_ixx8o8f5c_getWhen,
      displayName: 'emit',
    });
  const act_2820580198023098_showModal_pfw8tlge0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2820580198023098_showModal_pfw8tlge0_getActionArgs,
      displayName: 'createPassThroughInvoice',
      events: [inst_evh_2820580198023098_close_ixx8o8f5c],
    });
  const inst_evh_2820580198023098_click_d2omg1arl =
    new actionTypes.EventHandlerImpl({
      action: act_2820580198023098_showModal_pfw8tlge0,
      event: 'click',
      displayName: 'createPassThroughInvoice',
    });
  const inst_evh_2820580198023098_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2820580198023098_click_d2omg1arl],
    event: 'click',
  });
  const act_7708892537216822_emit_dg67dej3e = new actionTypes.EmitAction({
    actionArgs: act_7708892537216822_emit_dg67dej3e_getActionArgs,
  });
  const inst_evh_7708892537216822_success_z7ccvkjcq =
    new actionTypes.EventHandlerImpl({
      action: act_7708892537216822_emit_dg67dej3e,
      event: 'success',
      displayName: 'emit',
    });
  const act_7708892537216822_request_m7h3c8r0x = new actionTypes.RequestAction({
    actionArgs: act_7708892537216822_request_m7h3c8r0x_getActionArgs,
    displayName: 'createStornoInvoice',
    events: [inst_evh_7708892537216822_success_z7ccvkjcq],
  });
  const inst_evh_7708892537216822_click_pc0yhc1sw =
    new actionTypes.EventHandlerImpl({
      action: act_7708892537216822_request_m7h3c8r0x,
      event: 'click',
      displayName: 'createStornoInvoice',
    });
  const inst_evh_7708892537216822_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7708892537216822_click_pc0yhc1sw],
    event: 'click',
  });
  const act_3708615633814391_emit_tz6b7jd61 = new actionTypes.EmitAction({
    actionArgs: act_3708615633814391_emit_tz6b7jd61_getActionArgs,
  });
  const inst_evh_3708615633814391_success_xl2ssv9mw =
    new actionTypes.EventHandlerImpl({
      action: act_3708615633814391_emit_tz6b7jd61,
      event: 'success',
      displayName: 'emit',
    });
  const act_3708615633814391_request_iw01kndzd = new actionTypes.RequestAction({
    actionArgs: act_3708615633814391_request_iw01kndzd_getActionArgs,
    displayName: 'createCorrectorIvoice',
    events: [inst_evh_3708615633814391_success_xl2ssv9mw],
  });
  const inst_evh_3708615633814391_click_4uju92wbs =
    new actionTypes.EventHandlerImpl({
      action: act_3708615633814391_request_iw01kndzd,
      event: 'click',
      displayName: 'createCorrectorIvoice',
    });
  const inst_evh_3708615633814391_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3708615633814391_click_4uju92wbs],
    event: 'click',
  });
  const act_540979853027208_emit_aoo9btftv = new actionTypes.EmitAction({
    actionArgs: act_540979853027208_emit_aoo9btftv_getActionArgs,
  });
  const inst_evh_540979853027208_success_o928wchpx =
    new actionTypes.EventHandlerImpl({
      action: act_540979853027208_emit_aoo9btftv,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_540979853027208_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_540979853027208_success_o928wchpx],
    event: 'success',
  });
  const act_8845268238478512_emit_siy2j1ef6 = new actionTypes.EmitAction({
    actionArgs: act_8845268238478512_emit_siy2j1ef6_getActionArgs,
  });
  const inst_evh_8845268238478512_success_k29b1068i =
    new actionTypes.EventHandlerImpl({
      action: act_8845268238478512_emit_siy2j1ef6,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_8845268238478512_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8845268238478512_success_k29b1068i],
    event: 'success',
  });
  const act_8646800130370252_showModal_nfu1gboed =
    new actionTypes.ShowModalAction({
      actionArgs: act_8646800130370252_showModal_nfu1gboed_getActionArgs,
    });
  const inst_evh_8646800130370252_clickPreventStop_lcey6h1g9 =
    new actionTypes.EventHandlerImpl({
      action: act_8646800130370252_showModal_nfu1gboed,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8646800130370252_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8646800130370252_clickPreventStop_lcey6h1g9],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_3573211234170528_success_tzun5n9gv,
    inst_evh_3573211234170528_success,
    inst_evh_3350181908999980_success_1ln8b5d1j,
    inst_evh_3350181908999980_success,
    inst_evh_5072463219534047_success_intrz1pyx,
    inst_evh_5072463219534047_success,
    inst_evh_5450657804282831_success_rlm129vnb,
    inst_evh_5450657804282831_success,
    inst_evh_995920279152457_close_wjk5keq57,
    inst_evh_995920279152457_clickPreventStop_w0yj2wtlq,
    inst_evh_995920279152457_clickPreventStop,
    inst_evh_8434031504612555_clickPreventStop_6eg7ssqp2,
    reload: inst_evh_8434031504612555_clickPreventStop_6eg7ssqp2,
    inst_evh_8434031504612555_clickPreventStop,
    inst_evh_142876513986814_reload_2ujswgocl,
    inst_evh_142876513986814_reload,
    inst_evh_4619326685596221_clickPreventStop_ix0e6dw4d,
    inst_evh_4619326685596221_clickPreventStop,
    inst_evh_5071673642893579_close_20z2rv9c1,
    inst_evh_5071673642893579_click_gt4vfdi5h,
    registerInvoice: inst_evh_5071673642893579_click_gt4vfdi5h,
    inst_evh_5071673642893579_click,
    inst_evh_7200560637235153_success_pmyyy51mm,
    inst_evh_7200560637235153_click_1v4czf23a,
    createInvoicecopy: inst_evh_7200560637235153_click_1v4czf23a,
    inst_evh_7200560637235153_click,
    inst_evh_2820580198023098_close_ixx8o8f5c,
    inst_evh_2820580198023098_click_d2omg1arl,
    createPassThroughInvoice: inst_evh_2820580198023098_click_d2omg1arl,
    inst_evh_2820580198023098_click,
    inst_evh_7708892537216822_success_z7ccvkjcq,
    inst_evh_7708892537216822_click_pc0yhc1sw,
    createStornoInvoice: inst_evh_7708892537216822_click_pc0yhc1sw,
    inst_evh_7708892537216822_click,
    inst_evh_3708615633814391_success_xl2ssv9mw,
    inst_evh_3708615633814391_click_4uju92wbs,
    createCorrectorIvoice: inst_evh_3708615633814391_click_4uju92wbs,
    inst_evh_3708615633814391_click,
    inst_evh_540979853027208_success_o928wchpx,
    inst_evh_540979853027208_success,
    inst_evh_8845268238478512_success_k29b1068i,
    inst_evh_8845268238478512_success,
    inst_evh_8646800130370252_clickPreventStop_lcey6h1g9,
    inst_evh_8646800130370252_clickPreventStop,
  };

  const evh_3573211234170528_success_tzun5n9gv = async (event, scope) => {
    return await actions.inst_evh_3573211234170528_success_tzun5n9gv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3573211234170528_success = async (event, scope) => {
    return await actions.inst_evh_3573211234170528_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3350181908999980_success_1ln8b5d1j = async (event, scope) => {
    return await actions.inst_evh_3350181908999980_success_1ln8b5d1j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3350181908999980_success = async (event, scope) => {
    return await actions.inst_evh_3350181908999980_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5072463219534047_success_intrz1pyx = async (event, scope) => {
    return await actions.inst_evh_5072463219534047_success_intrz1pyx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5072463219534047_success = async (event, scope) => {
    return await actions.inst_evh_5072463219534047_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5450657804282831_success_rlm129vnb = async (event, scope) => {
    return await actions.inst_evh_5450657804282831_success_rlm129vnb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5450657804282831_success = async (event, scope) => {
    return await actions.inst_evh_5450657804282831_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_995920279152457_close_wjk5keq57 = async (event, scope) => {
    return await actions.inst_evh_995920279152457_close_wjk5keq57.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_995920279152457_clickPreventStop_w0yj2wtlq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_995920279152457_clickPreventStop_w0yj2wtlq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_995920279152457_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_995920279152457_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8434031504612555_clickPreventStop_6eg7ssqp2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8434031504612555_clickPreventStop_6eg7ssqp2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8434031504612555_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8434031504612555_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_142876513986814_reload_2ujswgocl = async (event, scope) => {
    return await actions.inst_evh_142876513986814_reload_2ujswgocl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_142876513986814_reload = async (event, scope) => {
    return await actions.inst_evh_142876513986814_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4619326685596221_clickPreventStop_ix0e6dw4d = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4619326685596221_clickPreventStop_ix0e6dw4d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4619326685596221_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4619326685596221_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5071673642893579_close_20z2rv9c1 = async (event, scope) => {
    return await actions.inst_evh_5071673642893579_close_20z2rv9c1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5071673642893579_click_gt4vfdi5h = async (event, scope) => {
    return await actions.inst_evh_5071673642893579_click_gt4vfdi5h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5071673642893579_click = async (event, scope) => {
    return await actions.inst_evh_5071673642893579_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7200560637235153_success_pmyyy51mm = async (event, scope) => {
    return await actions.inst_evh_7200560637235153_success_pmyyy51mm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7200560637235153_click_1v4czf23a = async (event, scope) => {
    return await actions.inst_evh_7200560637235153_click_1v4czf23a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7200560637235153_click = async (event, scope) => {
    return await actions.inst_evh_7200560637235153_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2820580198023098_close_ixx8o8f5c = async (event, scope) => {
    return await actions.inst_evh_2820580198023098_close_ixx8o8f5c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2820580198023098_click_d2omg1arl = async (event, scope) => {
    return await actions.inst_evh_2820580198023098_click_d2omg1arl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2820580198023098_click = async (event, scope) => {
    return await actions.inst_evh_2820580198023098_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7708892537216822_success_z7ccvkjcq = async (event, scope) => {
    return await actions.inst_evh_7708892537216822_success_z7ccvkjcq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7708892537216822_click_pc0yhc1sw = async (event, scope) => {
    return await actions.inst_evh_7708892537216822_click_pc0yhc1sw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7708892537216822_click = async (event, scope) => {
    return await actions.inst_evh_7708892537216822_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3708615633814391_success_xl2ssv9mw = async (event, scope) => {
    return await actions.inst_evh_3708615633814391_success_xl2ssv9mw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3708615633814391_click_4uju92wbs = async (event, scope) => {
    return await actions.inst_evh_3708615633814391_click_4uju92wbs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3708615633814391_click = async (event, scope) => {
    return await actions.inst_evh_3708615633814391_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_540979853027208_success_o928wchpx = async (event, scope) => {
    return await actions.inst_evh_540979853027208_success_o928wchpx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_540979853027208_success = async (event, scope) => {
    return await actions.inst_evh_540979853027208_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8845268238478512_success_k29b1068i = async (event, scope) => {
    return await actions.inst_evh_8845268238478512_success_k29b1068i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8845268238478512_success = async (event, scope) => {
    return await actions.inst_evh_8845268238478512_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8646800130370252_clickPreventStop_lcey6h1g9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8646800130370252_clickPreventStop_lcey6h1g9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8646800130370252_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8646800130370252_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpBox direction="column">
      <ItpText muted class="mb-0">
        {{ resourceTitle }}
      </ItpText>
      <ItpText tag="h4" :text="resourceKey" class="mb-0"> </ItpText>
    </ItpBox>
    <UcInvoiceTypeBadge
      :value="invoice.invoice_type"
      :kind="invoice.invoice_kind"
    >
    </UcInvoiceTypeBadge>
    <UcInvoiceStatusBadge :value="invoice.status"> </UcInvoiceStatusBadge>
    <div v-if="invoice.tags && invoice.tags.length">
      <span
        class="badge bg-secondary mx-1 shadow-none"
        v-for="tag in invoice.tags"
      >
        {{ tag }}
      </span>
    </div>
    <div :title="gettext('Closed')" v-if="invoice.isReadOnly">
      <ItpIcon :icon="$config.lockIcon"> </ItpIcon>
    </div>
    <div class="text-green-500" v-if="invoice.authority_transaction_id">
      <div :title="invoice.authority_transaction_id">
        <ItpIcon icon="fluent:cloud-sync-complete-16-regular"> </ItpIcon>
      </div>
      <div :title="JSON.stringify(invoice.authority_transaction_status)">
        <ItpIcon icon="fa-question-circle"> </ItpIcon>
      </div>
    </div>
    <ItpBox direction="column" v-if="invoice.reference_invoice">
      <ItpText muted class="mb-0">
        {{ pgettext('Invoice', 'Reference invoice') }}
      </ItpText>
      <UcInvoiceMiniCard :item="invoice.reference_invoice" no-icon>
      </UcInvoiceMiniCard>
    </ItpBox>
    <ItpBox
      direction="column"
      v-if="invoice.referencing_invoices && invoice.referencing_invoices.length"
    >
      <ItpText muted class="mb-0">
        {{ pgettext('Invoice', 'Referencing invoices') }}
      </ItpText>
      <ItpBox>
        <UcInvoiceMiniCard
          :key="referencingInvoice.id"
          :item="referencingInvoice"
          no-icon
          class="me-2"
          v-for="referencingInvoice in invoice.referencing_invoices"
        >
        </UcInvoiceMiniCard>
      </ItpBox>
    </ItpBox>
    <Icon
      :icon="'flagpack:' + invoice.language"
      :title="pgettext('Invoice', 'Invoice Language')"
      v-if="!invoice.isNew"
    >
    </Icon>
    <div class="btn-toolbar ms-auto flex-nowrap gap-2">
      <BtnIssueInvoice
        :invoice="invoice"
        v-if="
          invoice.meta && invoice.meta.issuance_status.status == 'not_started'
        "
        @success="evh_3573211234170528_success($event, {})"
      >
      </BtnIssueInvoice>
      <BtnSyncInvoice
        :invoice="invoice"
        v-if="
          invoice.meta &&
          invoice.meta.billing_service_status.status == 'not_started'
        "
        @success="evh_3350181908999980_success($event, {})"
      >
      </BtnSyncInvoice>
      <div class="ms-auto"></div>
      <BtnReportInvoiceData
        :resource="invoice"
        v-if="
          invoice.meta &&
          (invoice.meta.data_reporting_status.status == 'not_started' ||
            invoice.meta.data_reporting_status.status == 'error')
        "
        @success="evh_5072463219534047_success($event, {})"
      >
      </BtnReportInvoiceData>
      <BtnSyncResource
        :resource="invoice"
        target="irent"
        confirmMessage="A számlát csak egyszer lehet felölteni a régi rendszerbe,
feltöltés után a számlát már nem lehet módosítani."
        v-if="
          invoice.meta &&
          invoice.meta.sync_to_external_system_status.status == 'not_started'
        "
        @success="evh_5450657804282831_success($event, {})"
      >
      </BtnSyncResource>
      <ItpButton
        :icon="$config.editIcon"
        :tooltip="gettext('Edit')"
        variant="light"
        v-if="!invoice.isNew && !invoice.isReadOnly"
        @click.prevent.stop="evh_995920279152457_clickPreventStop($event, {})"
      >
      </ItpButton>
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        v-if="!invoice.isNew"
        @click.prevent.stop="evh_8434031504612555_clickPreventStop($event, {})"
      >
      </ItpButton>
      <UcInvoiceViewDocumentDropdown
        :invoice="invoice"
        @reload="evh_142876513986814_reload($event, {})"
      >
      </UcInvoiceViewDocumentDropdown>
      <ItpButton
        variant="light"
        icon="icon-park-outline:preview-open"
        :href="invoice.preview_url"
        :tooltip="pgettext('Invoice', 'Open preview')"
        target="_blank"
        use-auth
        show-spinner
        v-if="invoice.preview_url"
      >
      </ItpButton>
      <ItpButton
        variant="light"
        icon="icon-park-outline:log"
        :tooltip="pgettext('Invoice', 'Show log messages')"
        v-if="invoice.log_messages"
        @click.prevent.stop="evh_4619326685596221_clickPreventStop($event, {})"
      >
      </ItpButton>
      <BDropdown
        right
        variant="light"
        no-caret
        class="border"
        v-if="!invoice.isNew"
      >
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <ItpButton
          dropdown-item
          :text="pgettext('Invoice', 'Register invoice')"
          :tooltip="
            pgettext('Invoice', 'Add this invoice to the invoice register')
          "
          variant="default"
          v-if="invoiceIsRegistrable"
          @click="evh_5071673642893579_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnCopyResource', 'Copy')"
          :tooltip="pgettext('Invoice', 'Create new invoice from this one')"
          variant="default"
          @click="evh_7200560637235153_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('Invoice', 'Create pass-through')"
          :tooltip="messages.createPassThroughTooltip"
          variant="default"
          :disabled="!invoicePassThroughIsEnabled"
          v-if="!invoice.isNew"
          @click="evh_2820580198023098_click($event, {})"
        >
        </ItpButton>
        <hr />
        <ItpButton
          dropdown-item
          :text="pgettext('Invoice', 'Create storno')"
          :tooltip="createStornoTooltip"
          variant="default"
          :disabled="!invoiceIsReversible"
          v-if="!invoice.isNew"
          @click="evh_7708892537216822_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          variant="default"
          :text="pgettext('Invoice', 'Create corrector')"
          :tooltip="createCorrectorTooltip"
          :disabled="!invoiceIsModifiable"
          v-if="!invoice.isNew"
          @click="evh_3708615633814391_click($event, {})"
        >
        </ItpButton>
        <BtnLockResource
          no-icon
          dropdown-item
          :resource="invoice"
          :tooltip="pgettext('Invoice', 'Lock invoice')"
          v-if="!invoice.isNew && !invoice.isReadOnly"
          @success="evh_540979853027208_success($event, {})"
        >
        </BtnLockResource>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="invoice"
          :tooltip="pgettext('Invoice', 'Delete invoice')"
          v-if="!invoice.isNew && !invoice.isReadOnly"
          @success="evh_8845268238478512_success($event, {})"
        >
        </BtnDeleteResource>
        <hr />
        <ItpButton
          dropdown-item
          :text="pgettext('BtnResourceHistory', 'History')"
          :tooltip="pgettext('Invoice', 'Invoice history')"
          variant="default"
          @click.prevent.stop="
            evh_8646800130370252_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </BDropdown>
    </div>
  </ItpBox>
</template>
